import { formatLargeNumber } from "@/composables/formatUtils";
import type { DeviceInOtherGroup } from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";
import type { RegistryDevice } from "@/types";
import { apiErrorResponse } from "@/utils/apiErrors";
import type { TranslationFn } from "@/utils/countries";
import { countBy, groupBy, isPlainObject, mapValues, sumBy } from "lodash-es";

/** Below this many active devices the Devices tab offers no "Group devices" entry. */
export const DEVICE_GROUP_ENTRY_MIN_DEVICES = 10;

/** The member ids of each group, from the devices' own `device_group_id`. */
export const deviceIdsByGroup = (
  devices: readonly RegistryDevice[]
): Record<number, number[]> =>
  mapValues(
    groupBy(
      devices.filter((device) => device.device_group_id !== null),
      "device_group_id"
    ),
    (members) => members.map((device) => device.id)
  );

export type DeviceCountsByGroup = Partial<Record<number | "none", number>>;

/**
 * How many of the devices are in each group, keyed by group id, with the ungrouped ones under
 * `none`. The group chips and the "Add to group" menu both count with it, so they agree.
 */
export const deviceCountsByGroup = (
  devices: readonly RegistryDevice[]
): DeviceCountsByGroup =>
  countBy(devices, (device) => device.device_group_id ?? "none");

/**
 * Whether the device belongs to a group other than the one being edited, so it can't be picked for
 * it: a device is in at most one group.
 */
export const isInOtherGroup = (
  device: RegistryDevice,
  editingGroupId: number | null
): boolean =>
  device.device_group_id !== null && device.device_group_id !== editingGroupId;

/** The devices' summed capacity, e.g. "1 250", for a "… kW" label. */
export const totalPowerKw = (devices: readonly RegistryDevice[]): string =>
  formatLargeNumber(
    Math.round(sumBy(devices, (device) => device.device_power ?? 0))
  );

/** "N devices · X kW" for a set of devices. */
export const devicesPowerSummary = (
  devices: readonly RegistryDevice[],
  t: TranslationFn
): string =>
  t(
    "device_groups.devices_and_power",
    { count: devices.length, power: totalPowerKw(devices) },
    devices.length
  );

/**
 * @property matched - Devices to add, each once, in paste order
 * @property inOtherGroup - Devices that belong to a group other than the one being edited; not added
 * @property notFound - Pasted tokens matching no device, as typed
 * @property tokenCount - Distinct pasted tokens
 */
export interface PastedDeviceIds {
  matched: RegistryDevice[];
  inOtherGroup: RegistryDevice[];
  notFound: string[];
  tokenCount: number;
}

/**
 * Matches pasted device IDs to devices by Device ID (`device_eic`) or by name, case-insensitively.
 * Tokens split on newlines, commas, semicolons and whitespace, so a name containing a space can't be
 * pasted; the fleets this serves are named by GSRN or EIC.
 *
 * @param currentGroupId - The group being edited, whose own members count as matched
 */
export const parsePastedDeviceIds = (
  text: string,
  devices: readonly RegistryDevice[],
  currentGroupId: number | null = null
): PastedDeviceIds => {
  const tokens = [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((token) => token.trim())
        .filter(Boolean)
    ),
  ];
  const byKey = new Map<string, RegistryDevice>();
  // Names first so a Device ID wins when one device's name is another's ID.
  for (const device of devices) {
    if (device.device_name) byKey.set(device.device_name.toLowerCase(), device);
  }
  for (const device of devices) {
    byKey.set(device.device_eic.toLowerCase(), device);
  }

  const seen = new Set<number>();
  const result: PastedDeviceIds = {
    matched: [],
    inOtherGroup: [],
    notFound: [],
    tokenCount: tokens.length,
  };
  for (const token of tokens) {
    const device = byKey.get(token.toLowerCase());
    if (!device) {
      result.notFound.push(token);
    } else if (!seen.has(device.id)) {
      seen.add(device.id);
      (isInOtherGroup(device, currentGroupId)
        ? result.inOtherGroup
        : result.matched
      ).push(device);
    }
  }
  return result;
};

/** Whether an element of a `DEVICE_GROUP_DEVICES_IN_OTHER_GROUP` refusal has the declared shape. */
export const isDeviceInOtherGroup = (
  entry: unknown
): entry is DeviceInOtherGroup => {
  if (!isPlainObject(entry)) return false;
  const { device_id, device_eic, group_name } = entry as Record<
    string,
    unknown
  >;
  return (
    typeof device_id === "number" &&
    typeof device_eic === "string" &&
    (group_name === null || typeof group_name === "string")
  );
};

/**
 * A refused device group change worded for the user, naming the devices by Device ID, or null for a
 * failure the caller should hand to `globalErrorHandler`. The modal shows the message inline, the
 * Devices tab as a warning.
 */
export const deviceGroupErrorMessage = (
  error: unknown,
  t: TranslationFn
): string | null => {
  const body = apiErrorResponse(error)?.data;
  switch (body?.error_type) {
    case "DEVICE_GROUP_NAME_TAKEN":
      return t("device_groups.errors.name_taken");
    case "DEVICE_GROUP_LIMIT_REACHED":
      return t("device_groups.errors.limit_reached");
    case "DEVICE_GROUP_UNKNOWN_DEVICES":
      return t("device_groups.errors.unknown_devices");
    case "DEVICE_GROUP_DEVICES_IN_OTHER_GROUP": {
      const listed = body.error_data?.devices;
      // The message already says "another group", so a group without a name adds nothing to it.
      const names = (Array.isArray(listed) ? listed : [])
        .filter(isDeviceInOtherGroup)
        .map(({ device_eic, group_name }) =>
          group_name === null ? device_eic : `${device_eic} (${group_name})`
        );
      return t("device_groups.errors.devices_in_other_group", {
        devices: names.join(", "),
      });
    }
    default:
      return null;
  }
};

/** A proposed group of ungrouped devices sharing a site or a town. */
export interface AddressSuggestion {
  name: string;
  deviceIds: number[];
}

/** Fewer devices than this at one address are not worth a suggestion. */
export const ADDRESS_SUGGESTION_MIN_DEVICES = 3;

/** The group bar shows only the largest suggestions; dismissing one brings up the next. */
export const BAR_SUGGESTION_LIMIT = 3;

/**
 * Proposes groups from device addresses, which the registries format as "Street, City, ZIP": first
 * by street and city, then, for devices left over, by city alone. Only ungrouped devices count, a
 * cluster needs at least {@link ADDRESS_SUGGESTION_MIN_DEVICES}, and a suggestion whose name an
 * existing group already has (in any case) is skipped. Largest first.
 */
export const suggestGroupsFromAddress = (
  devices: readonly RegistryDevice[],
  existingGroupNames: readonly string[]
): AddressSuggestion[] => {
  const located = devices
    .filter((device) => device.device_group_id == null && device.address)
    .map((device) => ({ device, parts: addressParts(device.address ?? "") }))
    .filter(({ parts }) => parts.length > 0);

  const bySite = clusterBy(located, ({ parts }) =>
    parts.slice(0, 2).join(", ")
  );
  const siteSuggestions = bySite.filter(
    (cluster) => cluster.members.length >= ADDRESS_SUGGESTION_MIN_DEVICES
  );
  const inSite = new Set(
    siteSuggestions.flatMap((cluster) =>
      cluster.members.map(({ device }) => device.id)
    )
  );
  const byCity = clusterBy(
    located.filter(({ device }) => !inSite.has(device.id)),
    ({ parts }) => cityOf(parts)
  ).filter(
    (cluster) => cluster.members.length >= ADDRESS_SUGGESTION_MIN_DEVICES
  );

  const taken = new Set(existingGroupNames.map(normalise));
  const suggestions: AddressSuggestion[] = [];
  for (const cluster of [...siteSuggestions, ...byCity]) {
    const key = normalise(cluster.label);
    if (!cluster.label || taken.has(key)) continue;
    taken.add(key);
    suggestions.push({
      name: cluster.label,
      deviceIds: cluster.members.map(({ device }) => device.id),
    });
  }
  return suggestions.sort(
    (a, b) =>
      b.deviceIds.length - a.deviceIds.length || a.name.localeCompare(b.name)
  );
};

const normalise = (text: string): string =>
  text.normalize("NFKC").toLocaleLowerCase().replace(/\s+/g, " ").trim();

const addressParts = (address: string): string[] =>
  address
    .split(",")
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter(Boolean);

/** The town without a postal code, from "Street, City, ZIP" or a bare "City". */
const cityOf = (parts: readonly string[]): string =>
  (parts.length > 1 ? parts[1] : parts[0]).replace(/\d+/g, "").trim();

/** Clusters by normalised key, labelled with the first member's own spelling. */
const clusterBy = <M>(
  members: readonly M[],
  label: (member: M) => string
): { label: string; members: M[] }[] => {
  const clusters = new Map<string, { label: string; members: M[] }>();
  for (const member of members) {
    const memberLabel = label(member);
    const key = normalise(memberLabel);
    const cluster = clusters.get(key) ?? { label: memberLabel, members: [] };
    cluster.members.push(member);
    clusters.set(key, cluster);
  }
  return [...clusters.values()];
};
